import type { JSX } from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import { Tag } from "@impero-com/ui";
import { Col } from "antd";

import type { Guid } from "utils/guid";

import { TagCategory } from "../types";

import * as theme from "./theme.css";
import { BaseTag } from "./types";

const stringCompare = Cmp.string();

interface SelectedTagsProps<Tag extends BaseTag> {
    selectedTagIds: Guid[];
    getTagCategoryId: (tag: Tag) => Guid;
    allTags: Tag[];
    allTagCategories: TagCategory[];
    onRemoveTag: (tagId: string) => void;
    disabled?: boolean;
    readOnlyTagIds?: Guid[];
}

interface ResolvedTag {
    id: Guid;
    tagName: string;
    categoryName: string;
}

function SelectedTags<Tag extends BaseTag>({
    disabled,
    allTags,
    allTagCategories,
    selectedTagIds,
    getTagCategoryId,
    onRemoveTag,
    readOnlyTagIds = [],
}: SelectedTagsProps<Tag>): JSX.Element {
    const resolveTags = (tagIds: Guid[]): ResolvedTag[] =>
        tagIds
            .map((tagId) => {
                const tag = allTags.find((t) => t.id === tagId);
                if (tag === undefined) {
                    throw new Error(
                        `EditableTagList: Tag(id=${tagId}) is not found`
                    );
                }
                const category = allTagCategories.find(
                    (c) => c.id === getTagCategoryId(tag)
                );
                if (category === undefined) {
                    throw new Error(
                        `EditableTagList: TagCategory(id=${getTagCategoryId(
                            tag
                        )}) is not found, but Tag(id=${tagId}) refers to it`
                    );
                }
                return {
                    id: tag.id,
                    tagName: tag.name,
                    categoryName: category.name,
                };
            })
            .toSorted(
                Cmp.chain([
                    Cmp.map((t) => t.categoryName, stringCompare),
                    Cmp.map((t) => t.tagName, stringCompare),
                ])
            );

    const tags = resolveTags(selectedTagIds);
    const readOnlyTags = resolveTags(readOnlyTagIds);

    const tagElements = tags.map((tag) => {
        const handleTagRemove = () => onRemoveTag(tag.id);
        return (
            <Col key={tag.id} className={theme.tagElementsContainer}>
                <Tag closable={!disabled} onClose={handleTagRemove}>
                    <strong>{tag.categoryName}:</strong> {tag.tagName}
                </Tag>
            </Col>
        );
    });

    const readOnlyTagElements = readOnlyTags.map((tag) => (
        <Col key={`readonly-${tag.id}`} className={theme.tagElementsContainer}>
            <Tag variant="applied">
                <strong>{tag.categoryName}:</strong> {tag.tagName}
            </Tag>
        </Col>
    ));

    return (
        <>
            {readOnlyTagElements}
            {tagElements}
        </>
    );
}

export type { SelectedTagsProps };
export { SelectedTags };
